import React, { lazy as _lazy, useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'

import ThemeRoot from '../ThemeRoot'
import useUser from '../services/useUser'
import ErrorBoundary from '../components/ErrorBoundary'
import LegacyRedirect from '../components/LegacyRedirect'
import RedirectToEmployers from '../components/RedirectToEmployers'
import InstallPwaDialog from '../components/InstallPwaDialog/InstallPwaDialog'
import DpaAmendmentGate from '../components/DpaAmendmentGate/DpaAmendmentGate'
import InviteRedeem from './Login/InviteRedeem'
import { registerNativeLinkListener } from '../services/deepLink/nativeLinkListener'
import { registerNativePushTapListener } from '../services/deepLink/nativePushTapListener'
import { resumePendingActionIfAny } from '../services/deepLink/resumePendingAction'

function lazy(imported) {
  const LazyComponent = _lazy(imported)

  return props => (
    <ThemeRoot>
      <LazyComponent {...props} />
    </ThemeRoot>
  )
}

const Home = lazy(() => import('./Home'))
const Calculations = lazy(() => import('./Calculations'))
const OpeningBalancesCompletion = lazy(() => import('./OpeningBalances'))
const WageReplacements = lazy(() => import('./Employer/WageReplacements'))
const VariableComponents = lazy(() => import('./Employer/VariableComponents'))
const Calculation = lazy(() => import('./Calculation'))
const Timesheet = lazy(() => import('./Timesheet'))
const Timesheets = lazy(() => import('./Timesheets'))
const Report = lazy(() => import('./Report'))
const Holidays = lazy(() => import('./Holidays'))
const Holiday = lazy(() => import('./Holiday'))
const HolidayYear = lazy(() => import('./HolidayYear'))
const DaylightSaving = lazy(() => import('./DaylightSaving'))
const Cities = lazy(() => import('./Cities'))
const Login = lazy(() => import('./Login'))
const TwilioPhoneLogin = lazy(() => import('./Login/TwilioPhoneLogin'))
const SignUp = lazy(() => import('./SignUp'))
const SignedUp = lazy(() => import('./SignedUp'))
const OnboardingTypeSelect = lazy(() => import('./Onboarding/TypeSelect'))
const EmployeeOnboarding = lazy(() => import('./Onboarding/EmployeeOnboarding'))
const EmployerOnboarding = lazy(() => import('./Onboarding/EmployerOnboarding'))
const PayrollOnboarding = lazy(() => import('./Onboarding/PayrollOnboarding'))
const Attendance = lazy(() => import('./Attendance'))
const SickDayReport = lazy(() => import('./Attendance/SickDayReport'))
const VacationRequest = lazy(() => import('./Attendance/VacationRequest'))
const ReserveDutyReport = lazy(() => import('./Attendance/ReserveDutyReport'))
const AbsenceRequests = lazy(() => import('./Attendance/AbsenceRequests'))
const Notifications = lazy(() => import('./Notifications'))
const Permissions = lazy(() => import('./Permissions'))
const PermissionsCatalog = lazy(() => import('./Permissions/PermissionsCatalog'))
const AccrualBackfill = lazy(() => import('./AccrualBackfill'))
const UserPermissions = lazy(() => import('./UserPermissions'))
const Employers = lazy(() => import('./Employers'))
const Dashboard = lazy(() => import('./Dashboard'))
const ReportsFeed = lazy(() => import('./ReportsFeed'))
const AttendanceBoard = lazy(() => import('./AttendanceBoard'))
const AbsencesReview = lazy(() => import('./AbsencesReview'))
const ReportsHub = lazy(() => import('./ReportsHub'))
const Employer = lazy(() => import('./Employer'))
const MichpalMappingSettings = lazy(() => import('./Employer/MichpalMappingSettings'))
const SmsBillingComponents = lazy(() => import('./Employer/SmsBillingComponents'))
const TeamPermissions = lazy(() => import('./Employer/TeamPermissions'))
const AnomaliesReview = lazy(() => import('./Employer/AnomaliesReview'))
const RetroactiveChangesLog = lazy(() => import('./Employer/RetroactiveChangesLog'))
const ReportLog = lazy(() => import('./Report/ReportLog'))
const EmployeeDocuments = lazy(() => import('./EmployeeDocuments'))
const Agreements = lazy(() => import('./Agreements'))
const Agreement = lazy(() => import('./Agreement'))
const PayrollPreview = lazy(() => import('./PayrollPreview'))
const PayrollPreviewList = lazy(() => import('./PayrollPreviewList'))
const MonthlyApproval = lazy(() => import('./MonthlyApproval'))
const MonthClose = lazy(() => import('./MonthClose'))
const PeriodJournal = lazy(() => import('./PeriodJournal'))
const RetroactiveItems = lazy(() => import('./RetroactiveItems'))
const SignatureRequestsList = lazy(() => import('./Documents/SignatureRequests/SignatureRequestsList'))
const SignatureRequestDetail = lazy(() => import('./Documents/SignatureRequests/SignatureRequestDetail'))
// #378 Stage 7 (#1715): the unauthenticated employee-facing signing portal —
// see SigningPortal.js's own banner and signingPortal.js's PORTAL_LANDING_PATH.
const SigningPortal = lazy(() => import('./SigningPortal'))
// #2847 (spec 2110 §21.1): the public "system users' privacy policy" resource.
const SystemPrivacyPolicy = lazy(() => import('./SystemPrivacyPolicy'))

export default function AppRoutes() {
  const user = useUser()
  const navigate = useNavigate()

  // #1693, spec.md §6.1-6.2: cold start / backgrounded / already-open
  // Universal Link and App Link handling. Already-signed-in taps navigate
  // straight to the (allowlist-validated) target; a signed-out tap is
  // stored as a Pending Action by registerNativeLinkListener itself and
  // picked up by the effect below once `user` becomes truthy. No-op on web
  // (isNativePlatform() false) and when @capacitor/app isn't registered.
  useEffect(() => {
    return registerNativeLinkListener({
      hasSession: () => Boolean(user),
      onResolvedAction: (action) => navigate(`${action.path}${action.search || ''}`),
    })
  }, [user, navigate])

  // #1695, spec.md §7.3: "כל סוגי ההתראות משתמשים ב-deep link contract אחד
  // ל-Web ול-Native" — tapping a push notification goes through the exact
  // same normalizeDeepLink()/Pending-Action machinery as a Universal Link
  // tap above, just sourced from the push plugin's `pushTapped` event
  // instead of `appUrlOpen`. Same hasSession/onResolvedAction contract, same
  // no-op on web/unsynced-plugin builds.
  useEffect(() => {
    return registerNativePushTapListener({
      hasSession: () => Boolean(user),
      onResolvedAction: (action) => navigate(`${action.path}${action.search || ''}`),
    })
  }, [user, navigate])

  // #1693, spec.md §6.2: on the transition into a session, resume any
  // Pending Action stored while signed out — the server verifies it
  // (employer membership, TTL, nonce replay) before we navigate, and the
  // local record is consumed at most once regardless of the outcome. A
  // harmless no-op call when nothing was ever stored (e.g. every web
  // session, and every native session that never received a deep link).
  useEffect(() => {
    if (!user) return undefined
    let cancelled = false
    resumePendingActionIfAny().then(target => {
      if (!cancelled && target) {
        navigate(`${target.path}${target.search || ''}`)
      }
    })
    return () => {
      cancelled = true
    }
  }, [user, navigate])

  if (!user) {
    return <Routes>
      <Route path="/sign-up" element={<SignUp/>}/>
      <Route path="/login/twilio-phone" element={<TwilioPhoneLogin/>}/>
      <Route path="/signed-up" element={<SignedUp/>}/>
      {/* #378 Stage 7 (#1715): PORTAL_LANDING_PATH — where an unauthenticated
          signer's SMS/email link lands after handleEnter's redirect. */}
      <Route path="/sign-portal" element={<SigningPortal/>}/>
      {/* #2847 (spec 2110 §21.1): public, versioned. Must sit BEFORE the
          catch-all below, which would otherwise turn it into a login page. */}
      <Route path="/system-privacy-policy" element={<SystemPrivacyPolicy/>}/>
      <Route path="/system-privacy-policy/:version" element={<SystemPrivacyPolicy/>}/>
      <Route path="*" element={<Login/>}/>
    </Routes>
  }

  return (
    <ErrorBoundary>
      <InstallPwaDialog />
      {/* #2287 (spec 2110 §29): the first-activation DPA-amendment checkbox for
          a NEW employer's admin — renders nothing for everyone else. */}
      <DpaAmendmentGate />
      <Routes>
        <Route path="/" exact element={<Home/>}/>
        {/* Onboarding routes */}
        <Route path="/onboarding/type-select" exact element={<OnboardingTypeSelect/>}/>
        <Route path="/onboarding/employee" exact element={<EmployeeOnboarding/>}/>
        <Route path="/onboarding/employer" exact element={<EmployerOnboarding/>}/>
        <Route path="/onboarding/payroll" exact element={<PayrollOnboarding/>}/>
        {/* Attendance route for employees */}
        <Route path="/attendance" exact element={<Attendance/>}/>
        <Route path="/attendance/requests" exact element={<AbsenceRequests/>}/>
        <Route path="/attendance/sick-day" exact element={<SickDayReport/>}/>
        <Route path="/attendance/vacation" exact element={<VacationRequest/>}/>
        <Route path="/attendance/reserve-duty" exact element={<ReserveDutyReport/>}/>
        <Route path="/notifications" exact element={<Notifications/>}/>
        {/* Dynamic employer routes */}
        <Route path="/employers/:employerId/dashboard" exact element={<Dashboard/>}/>
        <Route path="/employers/:employerId/reports" exact element={<ReportsFeed/>}/>
        <Route path="/employers/:employerId/attendance-board" exact element={<AttendanceBoard/>}/>
        <Route path="/employers/:employerId/absences" exact element={<AbsencesReview/>}/>
        <Route path="/employers/:employerId/reports-hub" exact element={<ReportsHub/>}/>
        <Route path="/employers/:employerId/agreements" exact element={<Agreements/>}/>
        <Route path="/employers/:employerId/agreements/:id" exact element={<Agreement/>}/>
        <Route path="/employers/:employerId/calculations" exact element={<Calculations/>}/>
        {/* #237 task 4.10: the one-time completion screen for employees who
            predate the entitlement ledger. */}
        <Route path="/employers/:employerId/opening-balances" exact element={<OpeningBalancesCompletion/>}/>
        <Route path="/employers/:employerId/wage-replacements" exact element={<WageReplacements/>}/>
        <Route path="/employers/:employerId/variable-components" exact element={<VariableComponents/>}/>
        <Route path="/employers/:employerId/anomalies" exact element={<AnomaliesReview/>}/>
        {/* #489 (חלק ג' של #473): "הראה לי כל שינוי רטרואקטיבי ברבעון". */}
        <Route path="/employers/:employerId/retroactive-changes" exact element={<RetroactiveChangesLog/>}/>
        <Route path="/employers/:employerId/calculations/:id" exact element={<Calculation/>}/>
        {/* #726 — #288 spec.md §17/§36, the payroll draft screen for one
            employee's month ("מסך טיוטת תלוש לעובד"). */}
        <Route path="/employers/:employerId/calculations/:id/payroll-preview/:year/:month" exact element={<PayrollPreview/>}/>
        <Route path="/employers/:employerId/monthly-approval/:year/:month" exact element={<MonthlyApproval/>}/>
        {/* #886 — #248 spec.md §10-12, the month-close screen ("סגירת חודש"). */}
        <Route path="/employers/:employerId/month-close/:year/:month" exact element={<MonthClose/>}/>
        {/* #1128 — the payroll-period journal ("יומן תקופת השכר"), a
            read-only view over #894's 18 auditLog event types for one
            payroll period. Employer/admin only — see PeriodJournal.js's
            own `mayViewPeriodJournal`. */}
        <Route path="/employers/:employerId/period-journal/:year/:month" exact element={<PeriodJournal/>}/>
        {/* #985 — #892 spec.md §23ב, the employer's "בקשות ואירועים
            רטרואקטיביים לטיפול" queue. */}
        <Route path="/employers/:employerId/retroactive-items" exact element={<RetroactiveItems/>}/>
        {/* #730 — #288 spec.md §35, the "טיוטות שכר" list screen. */}
        <Route path="/employers/:employerId/payroll-previews" exact element={<PayrollPreviewList/>}/>
        <Route path="/employers/:employerId/calculations/:id/timesheets" exact element={<Timesheets/>}/>
        <Route path="/employers/:employerId/calculations/:id/documents" exact element={<EmployeeDocuments/>}/>
        {/* #378 Stage 7 (#1715): admin list/detail screens for signature
            requests — SignatureRequestWizard (#1760) had no entry point
            anywhere until this stage; see SignatureRequestsList.js. */}
        <Route path="/employers/:employerId/signature-requests" exact element={<SignatureRequestsList/>}/>
        <Route path="/employers/:employerId/signature-requests/:requestId" exact element={<SignatureRequestDetail/>}/>
        {/* A signed-in user (e.g. an admin testing their own link) can still
            land on a /sign-portal URL — keep it reachable here too rather
            than dead-ending on the authenticated Routes' lack of a catch-all. */}
        <Route path="/sign-portal" element={<SigningPortal/>}/>
        {/* #2847 (spec 2110 §21.1): the same public policy pages, for a signed-in user. */}
        <Route path="/system-privacy-policy" element={<SystemPrivacyPolicy/>}/>
        <Route path="/system-privacy-policy/:version" element={<SystemPrivacyPolicy/>}/>
        <Route path="/employers/:employerId/calculations/:calculationId/timesheets/:timesheetId" exact element={<Timesheet/>}/>
        <Route path="/employers/:employerId/calculations/:calculationId/timesheets/:timesheetId/report" exact element={<Report/>}/>
        <Route path="/employers/:employerId/calculations/:calculationId/timesheets/:timesheetId/report-log" exact element={<ReportLog/>}/>
        {/* Backward compatibility redirects - old /employer/* to new /employers/* */}
        <Route path="/employer/*" element={<RedirectToEmployers/>} />
        {/* Legacy routes - redirect to dynamic routes */}
        <Route path="/calculations/*" element={<LegacyRedirect/>}/>
        <Route path="/holidays" exact element={<Holidays/>}/>
        <Route path="/holidays/:id" exact element={<Holiday/>}/>
        <Route path="/holidays/:holidayId/years/:year" exact element={<HolidayYear/>}/>
        <Route path="/daylight-saving" exact element={<DaylightSaving/>}/>
        <Route path="/cities" element={<Cities/>}/>
        <Route path="/cities/:city" element={<Cities/>}/>
        <Route path="/cities/:city/:year" element={<Cities/>}/>
        <Route path="/accrual-backfill" exact element={<AccrualBackfill/>}/>
        <Route path="/permissions" exact element={<Permissions/>}/>
        {/* #380/#1808 0.2a: קטלוג §3.3 בלבד, לפני :email כדי שלא יתפרש כזיהוי משתמש. */}
        <Route path="/permissions/catalog" exact element={<PermissionsCatalog/>}/>
        <Route path="/permissions/:email" exact element={<UserPermissions/>}/>
        <Route path="/employers" exact element={<Employers/>}/>
        <Route path="/employers/:id" exact element={<Employer/>}/>
        {/* #296: מסך עצמאי, לא מוטמע בפרטי מעסיק. */}
        <Route path="/employers/:id/michpal-settings" exact element={<MichpalMappingSettings/>}/>
        {/* #380/#1878 (stage 3, 3.2, §8.4): הזנה ידנית של רכיב חיוב SMS. */}
        <Route path="/employers/:id/sms-billing-component" exact element={<SmsBillingComponents/>}/>
        {/* #887: מסך הרשאות עצמאי למעסיק — employerAdmin מעניק/מסיר
            MONTHLY_PAYROLL_APPROVER ו-MONTH_REOPEN בעצמו, בלי מפתח. */}
        <Route path="/employers/:id/team-permissions" exact element={<TeamPermissions/>}/>
        <Route path="/sign-up" element={<Navigate to="/" />} />
        <Route path="/login" element={<Navigate to="/" />} />
        {/* #325: an invite link opened by a user who is ALREADY signed in must
            still be redeemed. Sending these two to "/" dropped the query string
            with the invite in it, so an employee locked on a deleted
            calculation could never be re-linked. InviteRedeem falls back to "/"
            when the link carries no invite. */}
        <Route path="/signed-up" element={<InviteRedeem/>} />
        <Route path="/login/twilio-phone" element={<InviteRedeem/>} />
      </Routes>
    </ErrorBoundary>
  )
}
